import {
  ContactType,
  DoctorListDto,
  AppointmentResourceOccupationDto,
  WaitingListPriority,
  AppointmentStatusCodes,
} from '@lmt/shared-types';
import { SearchInputPatientData } from '@lmt/unicorn-shared/app/components/common/patient/PatientSearch';
import { DateTimeInterval } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DateTimeIntervalPicker';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { CaseData } from '@lmt/unicorn-shared/app/helpers/case-queries';
import { Interval } from 'date-fns';
import { ViewType } from 'devextreme/ui/scheduler';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { getEnumValuesDict } from '../../../patients/list/common/types';
import { SchedulerResourceToPick } from '../../assistant/scheduling-assistant-types';

export interface VisitCreatingFormData {
  patient: SearchInputPatientData | null;
  resources: SelectedResources;
  caseId: number | null;
  dateTimeInterval: DateTimeInterval;
  alertPatient: number | null;
  waitingList: boolean;
  priority: WaitingListPriority | null;
  note: string | null;
  visitTypeId: number | null;
  priorityContactType: ContactType | null;
  takenTimeSlots: Interval[];
  attendingDoctorId?: number | null;
  statusId?: AppointmentStatusCodes | null;
}

export interface VisitType {
  id: number;
  label: string;
  specificStartTimeInSecondsSinceMidnight?: number | null;
  defaultDurationInSeconds?: number;
}

export interface VisitCreatingFormComponentProps {
  form: UseFormReturn<VisitCreatingFormData>;

  cases: CaseData[];
  caseId?: number;
  alertPatientOptions: AlertPatientOption[];
  visitTypes: VisitType[];
  addNewPatient: (
    selectInInput?: (patient: SearchInputPatientData) => void,
  ) => void;
  addNewCase: (patientId?: number) => void;
  onChangeCase: (caseId: number) => Promise<void>;
  selectPatient: (patient: SearchInputPatientData | null) => void;
  setResourceSelected: (
    type: string,
    resourceId: number,
    selected: boolean,
  ) => void;
}

export interface VisitCreatingExpandedFormProps
  extends VisitCreatingFormComponentProps {
  takenTimeSlotsForResources: TakenTimeSlotsForResources;
  resourceSettings: ResourceSettings;
  resourceGroups: ResourceGroups;
  selectedResources: SelectedResources;
  resourcesLoaded: boolean;
  takenTimeSlots: Interval[];
  visitId?: number;
  isWeekViewByDefault?: boolean;
  attendingDoctors: DoctorListDto[];
  onAttendingDoctorsSearch: (value: string | null) => void;
  onStatusChange: (newStatusValue: boolean) => void;
  isAttendingDoctorsLoading: boolean;
  timeSlotTimeStepInMinutes?: number;
  typesWithAnyAvailableResource: string[];
  setTypesWithAnyAvailableResource: (types: string[]) => void;
}

export interface VisitCreatingMiniFormProps
  extends VisitCreatingFormComponentProps {
  eventTypeSelection: JSX.Element | null;
}

export type VisitCreatingOrEditingFormProps = FormComponentProps & {
  eventTypeSelection: JSX.Element | null;
  selectedInterval?: Interval;
  visitId?: number | undefined | null;
  assignedResources?: number[];
  patientId?: number;
  isWeekView?: boolean;
  caseId?: number;
  doctorId?: number;
  visitTypeId?: number | undefined | null;
  timeSlotTimeStepInMinutes?: number;
  calendarType?: ViewType;
  defaultVisitStartTimeInSeconds?: number;
};

export interface AlertPatientOption {
  label: string;
  intervalBeforeInMills: number;
}

/**
 * Key is a type of a resource (e.g. Place, Doctor, Resource)
 * Value is a list of occupied intervals for resources of that type
 */
export interface TakenTimeSlotsForResources {
  [type: string]: TakenTimeSlotsForResource[];
}

interface TakenTimeSlotsForResource {
  resourceId: number;
  slots: Interval[];
}

export interface SelectedResources {
  [type: string]: ResourceToPick[];
}

export interface ResourceGroups {
  [type: string]: ResourceToPick[];
}

export type ResourceToPick = SchedulerResourceToPick & {
  getTimeSlotsFromAppointments: (
    appointments: AppointmentResourceOccupationDto[],
  ) => Interval[];
};

export interface ResourceSettings {
  [type: string]: {
    isMultiSelect: boolean;
    isEmbedded?: boolean;
  };
}
export const patientResourceType = 'Patient';
export const newCaseId = -1;

export const useVisitStatusOptions = () =>
  useMemo(() => getEnumValuesDict(AppointmentStatusCodes, 'visit_status'), []);
